import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/*
 * Koriderm storefront components (portados de Longevita).
 * Every component renders complete, usable markup from Liquid; this module only enhances it.
 */

/** @param {Element} element */
function isRTL(element) {
  return getComputedStyle(element).direction === 'rtl';
}

/* ---------------------------------------------------------------------------
 * Reveal on scroll
 * Content is only hidden after this module confirms it can reveal it again.
 * ------------------------------------------------------------------------ */

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @param {ParentNode} root */
function observeReveals(root) {
  if (!revealObserver) return;

  for (const element of root.querySelectorAll('[data-kd-reveal]:not(.is-visible)')) {
    revealObserver.observe(element);
  }
}

if ('IntersectionObserver' in window && !prefersReducedMotion()) {
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  document.documentElement.classList.add('kd-reveal-ready');
  observeReveals(document);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observeReveals(event.target);
  });
}

/* ---------------------------------------------------------------------------
 * <kd-carousel>: horizontal scroller with previous / next buttons.
 * With `data-autoplay="<ms>"` it advances one item per interval and returns to the first item
 * after the last. Autoplay pauses on hover, focus, touch, off-screen and hidden tabs, can be
 * paused with the optional toggle button, and never runs with reduced motion.
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} KdCarouselRefs
 * @property {HTMLElement} track - The scroll container.
 * @property {HTMLButtonElement} [prev] - Previous button.
 * @property {HTMLButtonElement} [next] - Next button.
 * @property {HTMLButtonElement} [toggle] - Autoplay pause / play button.
 *
 * @extends Component<KdCarouselRefs>
 */
class KdCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  /** @type {number} */
  #timer = 0;

  /** Reasons autoplay is currently held back (focus, touch, offscreen). */
  #holds = new Set();

  /** Paused by the visitor through the toggle button. */
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    window.clearInterval(this.#timer);
  }

  #bind() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#updateButtons();
    this.#bindAutoplay(signal);
  }

  /** @param {AbortSignal} signal */
  #bindAutoplay(signal) {
    this.#observer?.disconnect();
    window.clearInterval(this.#timer);
    this.#holds.clear();

    const delay = Number(this.dataset.autoplay);
    if (!Number.isFinite(delay) || delay <= 0 || prefersReducedMotion()) return;

    const { toggle } = this.refs;
    if (toggle) toggle.hidden = false;
    this.#syncToggle();

    /** @param {string} reason @param {boolean} active */
    const hold = (reason, active) => {
      if (active) {
        this.#holds.add(reason);
      } else {
        this.#holds.delete(reason);
      }
      this.#restartAutoplay();
    };

    this.addEventListener('focusin', () => hold('focus', true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) hold('focus', false);
      },
      { signal }
    );
    this.refs.track.addEventListener('touchstart', () => hold('touch', true), { passive: true, signal });
    for (const type of ['touchend', 'touchcancel']) {
      this.refs.track.addEventListener(type, () => hold('touch', false), { passive: true, signal });
    }
    document.addEventListener('visibilitychange', () => this.#restartAutoplay(), { signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) hold('offscreen', !entry.isIntersecting);
      });
      this.#observer.observe(this);
    }

    this.#restartAutoplay();
  }

  /** Restarts the interval from zero, or stops it while anything holds autoplay back. */
  #restartAutoplay() {
    window.clearInterval(this.#timer);

    const delay = Number(this.dataset.autoplay);
    if (this.#userPaused || this.#holds.size > 0 || document.hidden) return;

    this.#timer = window.setInterval(() => this.#advance(), delay);
  }

  /** Moves one item forward, or back to the first item after the last. */
  #advance() {
    // Hover is read per tick: enter/leave events go stale when the page scrolls under a still mouse,
    // and touch screens keep a sticky :hover after a tap.
    if (window.matchMedia('(hover: hover)').matches && this.matches(':hover')) return;

    const { track } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 4) return;

    if (Math.abs(track.scrollLeft) >= max - 4) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.#scroll(1);
    }
  }

  #syncToggle() {
    const { toggle } = this.refs;
    this.toggleAttribute('data-autoplay-paused', this.#userPaused);
    if (!toggle) return;

    const label = this.#userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
    if (label) toggle.setAttribute('aria-label', label);
  }

  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    this.#syncToggle();
    this.#restartAutoplay();
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateButtons());
  };

  #updateButtons() {
    const { track, prev, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = max > 4;

    this.toggleAttribute('data-scrollable', scrollable);
    if (prev) prev.disabled = !scrollable || position <= 4;
    if (next) next.disabled = !scrollable || position >= max - 4;
  }

  /** Width of one item plus the track gap. */
  #step() {
    const { track } = this.refs;
    const item = track.firstElementChild;
    if (!(item instanceof HTMLElement)) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  /** @param {number} direction */
  #scroll(direction) {
    const multiplier = isRTL(this) ? -1 : 1;
    this.refs.track.scrollBy({
      left: this.#step() * direction * multiplier,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }
}

/* ---------------------------------------------------------------------------
 * <kd-counter>: counts up to its final value when scrolled into view.
 * Markup already contains the final value for no-JS and reduced-motion users.
 * ------------------------------------------------------------------------ */

class KdCounter extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    const target = Number(this.dataset.value);
    if (!Number.isFinite(target) || prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    if (this.dataset.animated === 'true') return;

    this.textContent = this.#format(0);
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#animate(target);
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {number} value */
  #format(value) {
    const decimals = Number(this.dataset.decimals) || 0;
    return value.toLocaleString(document.documentElement.lang || undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /** @param {number} target */
  #animate(target) {
    const duration = Number(this.dataset.duration) || 1600;
    const start = performance.now();

    /** @param {number} now */
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = this.#format(target * eased);

      if (progress < 1) {
        this.#frame = requestAnimationFrame(tick);
      } else {
        this.dataset.animated = 'true';
      }
    };

    this.#frame = requestAnimationFrame(tick);
  }
}

/* ---------------------------------------------------------------------------
 * <kd-gallery>: product image slider with thumbnails.
 * ------------------------------------------------------------------------ */

/**
 * @typedef {object} KdGalleryRefs
 * @property {HTMLElement} track - Slides scroll container.
 * @property {HTMLElement[]} [slides] - Slides.
 * @property {HTMLButtonElement[]} [thumbs] - Thumbnail buttons.
 *
 * @extends Component<KdGalleryRefs>
 */
class KdGallery extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #bind() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#syncThumbs());
      },
      { passive: true, signal: this.#abortController.signal }
    );
  }

  get #index() {
    const { track } = this.refs;
    return Math.round(Math.abs(track.scrollLeft) / Math.max(track.clientWidth, 1));
  }

  #syncThumbs() {
    const index = this.#index;
    for (const [position, thumb] of (this.refs.thumbs ?? []).entries()) {
      if (position === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /** @param {number} index */
  goTo(index) {
    const slides = this.refs.slides ?? [];
    const count = slides.length;
    if (!count) return;

    const target = slides[(index + count) % count];
    if (!target) return;

    const multiplier = isRTL(this) ? -1 : 1;
    this.refs.track.scrollTo({
      left: target.offsetLeft * multiplier,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  previous() {
    this.goTo(this.#index - 1);
  }

  next() {
    this.goTo(this.#index + 1);
  }

  /** @param {Event} event */
  selectThumb(event) {
    const thumb = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(thumb instanceof HTMLElement)) return;
    this.goTo(Number(thumb.dataset.index));
  }
}

/* ---------------------------------------------------------------------------
 * <kd-buy-now>: the store has no cart page. Submitting the product form replaces the cart with
 * the chosen pack through the Cart API (`/cart/clear.js` then `/cart/add.js`) and continues to
 * /checkout. Going through the Cart API, instead of a cart permalink, lets Shopify's storefront
 * events (and the Meta pixel on top of them) register AddToCart. Clearing first keeps the
 * checkout to this pack even if the visitor comes back with the browser's back button.
 * If either request fails or takes too long it falls back to the cart permalink
 * (`/cart/<variant>:<quantity>`), and without JS the form posts to /cart/add with
 * `return_to=/checkout`.
 * ------------------------------------------------------------------------ */

const KD_BUY_TIMEOUT_MS = 4000;

class KdBuyNow extends Component {
  /** @param {SubmitEvent} event - Routed by `on:submit`, so `event.target` is this element. */
  buy(event) {
    const form = this.querySelector('form');
    if (!form) return;

    const data = new FormData(form);
    const variantId = data.get('id');
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);
    if (!variantId) return;

    event.preventDefault();
    if (this.dataset.busy === 'true') return;
    this.dataset.busy = 'true';

    const button = form.querySelector('button[name="add"]');
    if (button instanceof HTMLButtonElement) button.setAttribute('aria-busy', 'true');

    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    const permalink = `${cartUrl}/${variantId}:${quantity}`;

    this.#addToCart(cartUrl, Number(variantId), quantity).then(
      () => window.location.assign('/checkout'),
      () => window.location.assign(permalink)
    );
  }

  /**
   * @param {string} cartUrl
   * @param {number} id - Variant id.
   * @param {number} quantity
   */
  async #addToCart(cartUrl, id, quantity) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), KD_BUY_TIMEOUT_MS);
    const request = (path, body) =>
      fetch(`${cartUrl}/${path}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      }).then((response) => {
        if (!response.ok) throw new Error(`${path} ${response.status}`);
      });

    try {
      await request('clear', {});
      await request('add', { items: [{ id, quantity }] });
    } finally {
      window.clearTimeout(timer);
    }
  }
}

// Returning from checkout through the back button restores the busy state from the bfcache.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const button of document.querySelectorAll('kd-buy-now button[aria-busy]')) {
    button.removeAttribute('aria-busy');
  }
  for (const buyNow of document.querySelectorAll('kd-buy-now[data-busy]')) {
    delete (/** @type {HTMLElement} */ (buyNow).dataset.busy);
  }
});

/* ---------------------------------------------------------------------------
 * <kd-pack-picker>: radio cards that choose the variant submitted by the surrounding
 * <kd-buy-now> form. Each radio carries the variant id, price, label and availability; the change is
 * mirrored on every buy button, price and pack label of the section (hero and sticky bar).
 * ------------------------------------------------------------------------ */

class KdPackPicker extends Component {
  /** @param {Event} event */
  select(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const productForm = this.closest('kd-buy-now');
    if (!productForm) return;

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = input.value;

    // The hero and the sticky buy bar share these hooks, so update every copy in the section.
    const scope = this.closest('[data-kd-scope], .kd-pdp') ?? productForm;

    // Other pickers in the same scope (advertorial offer boxes and the sticky bar) follow this choice.
    for (const other of scope.querySelectorAll('kd-pack-picker')) {
      if (other === this) continue;
      for (const radio of other.querySelectorAll('input[type="radio"]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        radio.checked = radio.value === input.value;
      }
      const otherForm = other.closest('kd-buy-now');
      const otherVariant = otherForm?.querySelector('input[name="id"]');
      if (otherVariant instanceof HTMLInputElement) otherVariant.value = input.value;
    }

    for (const price of scope.querySelectorAll('[data-kd-atc-price]')) {
      price.textContent = input.dataset.price ?? '';
    }

    for (const label of scope.querySelectorAll('[data-kd-pack-label]')) {
      label.textContent = input.dataset.label ?? '';
    }

    const available = input.dataset.available === 'true';
    for (const button of scope.querySelectorAll('button[data-kd-buy-button]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = !available;
      const label = button.querySelector('[data-kd-atc-label]');
      if (label) label.textContent = (available ? this.dataset.addLabel : this.dataset.soldOutLabel) ?? '';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', input.value);
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

/* ---------------------------------------------------------------------------
 * <kd-sticky-buy>: fixed buy bar on phones. It slides in while the main buy button (the element
 * matched by `data-target`) is out of view and slides out when it is visible again. Its button
 * submits the hero product form through the `form` attribute, so the purchase logic stays in
 * <kd-buy-now>. `data-target` may match several elements (advertorial offer boxes): the bar hides
 * while any of them is on screen. Optional `data-after` is a selector for an element (the headline)
 * that must have scrolled out above the viewport before the bar first appears.
 * Without IntersectionObserver it simply stays visible.
 * ------------------------------------------------------------------------ */

class KdStickyBuy extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.hidden = false;
    const targets = this.dataset.target ? [...document.querySelectorAll(this.dataset.target)] : [];
    const after = this.dataset.after ? document.querySelector(this.dataset.after) : null;

    if (targets.length === 0 || !('IntersectionObserver' in window)) {
      this.#setVisible(true);
      return;
    }

    this.#setVisible(false);
    const onScreen = new Set();
    let pastAfter = !after;
    const update = () => this.#setVisible(pastAfter && onScreen.size === 0);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === after) {
          pastAfter = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        } else if (entry.isIntersecting) {
          onScreen.add(entry.target);
        } else {
          onScreen.delete(entry.target);
        }
      }
      update();
    });
    for (const target of targets) this.#observer.observe(target);
    if (after) this.#observer.observe(after);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.inert = !visible;
    this.setAttribute('aria-hidden', String(!visible));
  }
}

/* ---------------------------------------------------------------------------
 * <kd-compare>: before/after comparator. Dragging anywhere on the frame (mouse or touch, vertical
 * page scroll still works) or using the arrow keys on the native range input moves the divider.
 * The position lives in --kd-compare-pos; without JS the photos stay split at 50%.
 * ------------------------------------------------------------------------ */

class KdCompare extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const frame = this.querySelector('.kd-compare__frame');
    const range = this.querySelector('.kd-compare__range');
    if (!(frame instanceof HTMLElement) || !(range instanceof HTMLInputElement)) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    /** @param {number} value */
    const setPosition = (value) => {
      const clamped = Math.min(Math.max(value, 0), 100);
      this.style.setProperty('--kd-compare-pos', `${clamped}%`);
      range.value = String(Math.round(clamped));
    };

    /** @param {PointerEvent} event */
    const fromPointer = (event) => {
      const rect = frame.getBoundingClientRect();
      let ratio = (event.clientX - rect.left) / Math.max(rect.width, 1);
      if (isRTL(this)) ratio = 1 - ratio;
      setPosition(ratio * 100);
    };

    let dragging = false;
    frame.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        dragging = true;
        frame.setPointerCapture(event.pointerId);
        fromPointer(event);
      },
      { signal }
    );
    frame.addEventListener('pointermove', (event) => dragging && fromPointer(event), { signal });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      frame.addEventListener(type, () => (dragging = false), { signal });
    }
    range.addEventListener('input', () => setPosition(Number(range.value)), { signal });

    setPosition(Number(range.value) || 50);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }
}

/* ---------------------------------------------------------------------------
 * <kd-timeline>: fills the center line as the visitor scrolls through the steps.
 * ------------------------------------------------------------------------ */

class KdTimeline extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    if (prefersReducedMotion()) {
      this.style.setProperty('--kd-progress', '1');
      return;
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const rect = this.getBoundingClientRect();
    const anchor = window.innerHeight * 0.6;
    const progress = Math.min(Math.max((anchor - rect.top) / Math.max(rect.height, 1), 0), 1);
    this.style.setProperty('--kd-progress', progress.toFixed(3));
  }
}

/* ---------------------------------------------------------------------------
 * Offer links ([data-kd-select="<variant id>"]): preselect that pack in the buy box before the
 * anchor scrolls to it. Without JS they are plain links to #kd-buy-box.
 * ------------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-kd-select]') : null;
  if (!(link instanceof HTMLElement)) return;

  const input = document.querySelector(`.kd-pack__input[value="${CSS.escape(link.dataset.kdSelect ?? '')}"]`);
  if (!(input instanceof HTMLInputElement) || input.disabled || input.checked) return;

  input.checked = true;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

const definitions = /** @type {const} */ ([
  ['kd-carousel', KdCarousel],
  ['kd-counter', KdCounter],
  ['kd-gallery', KdGallery],
  ['kd-buy-now', KdBuyNow],
  ['kd-pack-picker', KdPackPicker],
  ['kd-sticky-buy', KdStickyBuy],
  ['kd-compare', KdCompare],
  ['kd-timeline', KdTimeline],
]);

for (const [name, constructor] of definitions) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
